import {useMemo} from 'react';

import useMoleculeSchemasPairing from 'chemistry/bulk-upload/useMoleculeSchemasPairing';
import type {SchemaPairing} from 'chemistry/bulk-upload/useMoleculeSchemasPairing';
import type {TagSchema} from 'constants/RegistryConstants';

export enum MismatchStatus {
  OK = 'ok',
  WARNING = 'warning',
  BLOCKED = 'blocked',
}

enum SchemaRole {
  FORM = 'form',
  COMPOUND_WITH_FORM = 'compound_with_form',
  UNKNOWN = 'unknown',
}

export type SchemaMismatchResult =
  | {status: MismatchStatus.OK}
  | {status: MismatchStatus.WARNING; message: string; autoSwitchSchema: TagSchema; targetSchemaName: string}
  | {status: MismatchStatus.BLOCKED; message: string};

interface UseSchemaMismatchDetectionParams {
  tagSchemaId: string | null;
  hasCounterparts: boolean;
  registryId: string | null;
}

function getSchemaRole(schemaId: string, pairing: SchemaPairing | undefined): SchemaRole {
  if (!pairing) {
    return SchemaRole.UNKNOWN;
  }
  if (pairing.formSchemaId === schemaId) {
    return SchemaRole.FORM;
  }
  if (pairing.compoundSchemaId === schemaId) {
    return SchemaRole.COMPOUND_WITH_FORM;
  }
  return SchemaRole.UNKNOWN;
}

export default function useSchemaMismatchDetection({
  tagSchemaId,
  hasCounterparts,
  registryId,
}: UseSchemaMismatchDetectionParams): SchemaMismatchResult {
  const {pairingMap, schemasById, isLoading} = useMoleculeSchemasPairing(registryId);

  return useMemo((): SchemaMismatchResult => {
    if (isLoading || tagSchemaId == null) {
      return {status: MismatchStatus.OK};
    }

    const pairing = pairingMap[tagSchemaId];
    const role = getSchemaRole(tagSchemaId, pairing);

    if (hasCounterparts) {
      if (role === SchemaRole.FORM) {
        return {status: MismatchStatus.OK};
      }
      if (role === SchemaRole.COMPOUND_WITH_FORM) {
        const targetSchema = schemasById[pairing.formSchemaId];
        if (!targetSchema) {
          return {status: MismatchStatus.OK};
        }
        return {
          status: MismatchStatus.WARNING,
          message: `The structure contains salts or solvents. The schema has been switched to ${pairing.formSchemaName}.`,
          autoSwitchSchema: targetSchema,
          targetSchemaName: pairing.formSchemaName,
        };
      }
      return {
        status: MismatchStatus.BLOCKED,
        message:
          'This structure contains salts or solvents, but the selected schema has no linked Molecule Form schema to support them. Select a schema with a linked Form, or simplify the structure.',
      };
    }

    if (role === SchemaRole.FORM) {
      const targetSchema = schemasById[pairing.compoundSchemaId];
      if (!targetSchema) {
        return {status: MismatchStatus.OK};
      }
      return {
        status: MismatchStatus.WARNING,
        message: `No salts or solvents were detected. The schema has been switched to ${pairing.compoundSchemaName}.`,
        autoSwitchSchema: targetSchema,
        targetSchemaName: pairing.compoundSchemaName,
      };
    }

    return {status: MismatchStatus.OK};
  }, [isLoading, tagSchemaId, pairingMap, schemasById, hasCounterparts]);
}
